import { WithOwner } from "../IDBSchema";
import { EntityDataApi } from "../IndexedDBApi";
import { LotBase, LotBasePatchPayload, LotBasePutPayload } from "../../../schema/generated/Lot";
import { getFromIdPayload, setDeletePayload } from "../../../schema/general";
import { lotRowBase, lotRowsBase } from "../../../schema/console/lot";
import { PutStatusResponse, StatusResponse } from "../../../schema/console/statusData";
import { qrScanProcessPayload } from "../../../schema/console/qrScan";
import { LotForMaterialSearch } from "../../../schema/console/material";
import { idbEntityDefs } from "../generated/entityDefs";
import Time from "../../../util/Time";
import { required } from "../../../util/typed";

export class LotDataApi extends EntityDataApi<LotBase> {
  public readonly def = idbEntityDefs.Lot;

  public async getAll(ownerId: string): Promise<LotBase[]> {
    return await this.listByOwner(ownerId);
  }

  public async getById(payload: WithOwner<getFromIdPayload>): Promise<LotBase | null> {
    return await this.findById(payload);
  }

  public async getLotForMaterialSearch(payload: WithOwner<getFromIdPayload>): Promise<LotForMaterialSearch | null> {
    const lot = await this.getById(payload);
    if (!lot) {
      return null;
    }
    const carryOut = await this.IDB().carryOutDataApi.getRowById({ id: lot.carry_out_id, ownerId: payload.ownerId });
    if (!carryOut) {
      return null;
    }
    const user = await this.IDB().userDataApi.getFullDataById({ id: lot.preparer_user_id, ownerId: payload.ownerId });
    return { ...lot, CarryOut: carryOut, User: user };
  }

  public async getRowById(payload: WithOwner<getFromIdPayload>): Promise<lotRowBase | null> {
    const lot = await this.getById(payload);
    if (!lot) {
      return null;
    }
    const materials = await this.IDB().materialDataApi.getByLotId(lot.id, payload.ownerId);
    const user = await this.IDB().userDataApi.getFullDataById({ id: lot.preparer_user_id, ownerId: payload.ownerId });
    return { ...lot, Materials: materials, User: user };
  }

  public async getRowsByCarryOutId(payload: WithOwner<getFromIdPayload>): Promise<lotRowsBase> {
    const [lots, materials] = await Promise.all([
      this.getByCarryOutId(payload.id, payload.ownerId),
      this.IDB().materialDataApi.getAll(payload.ownerId),
    ]);
    const userIds = [...new Set(lots.map((c) => c.preparer_user_id))];
    const users = await Promise.all(
      userIds.map((id) =>
        this.IDB().userDataApi.getFullDataById({ id, ownerId: payload.ownerId })
      )
    );
    const userMap = new Map(users.map((u) => [u.id, u]));
    return lots.map((lot) => ({
      ...lot,
      Materials: materials.filter((material) => material.lot_id === lot.id),
      User: required(userMap.get(lot.preparer_user_id), "登録者ユーザー"),
    }));
  }

  public async getByCarryOutId(carryOutId: string, ownerId: string): Promise<LotBase[]> {
    return await this.readByIndex("carry_out_id", ownerId, carryOutId);
  }

  // QRスキャンによる出荷処理。Lotの出荷時刻 patch と材料行の紐付け更新を1トランザクションで
  // まとめて書き込む(途中失敗で親だけ/材料だけ更新される中途半端な状態を防ぐ)。
  public async process(payload: WithOwner<qrScanProcessPayload>): Promise<StatusResponse> {
    const [lot, materials] = await Promise.all([
      this.getById(payload),
      this.IDB().materialDataApi.getAll(payload.ownerId),
    ]);
    if (!lot) {
      throw new Error("Lotがありません。");
    }

    const selected = new Set(payload.MaterialIDs);
    const toDetach = materials.filter((m) => m.lot_id === payload.id && !selected.has(m.id)).map((m) => ({ id: m.id, lot_id: null }));
    const toAttach = payload.MaterialIDs.map((id) => ({ id, lot_id: payload.id }));
    const materialRows = await this.IDB().materialDataApi.buildLinkRows([...toAttach, ...toDetach], payload.ownerId);

    const lotRow = { ...lot, shipped_at: Time.Now().GetTime(), ...this.addPatchBaseColumns() };

    await this.IDB().putRowsAtomic([
      { store: this.def.store, rows: [lotRow] },
      { store: this.IDB().materialDataApi.def.store, rows: materialRows },
    ]);

    return { status: "ok" };
  }

  public async put(data: WithOwner<LotBasePutPayload>): Promise<PutStatusResponse> {
    return await this.createRow(data);
  }

  public async patch(data: WithOwner<LotBasePatchPayload>): Promise<StatusResponse> {
    return await this.patchRow(data);
  }

  public async delete(data: WithOwner<setDeletePayload>): Promise<StatusResponse> {
    return await this.softDelete(data);
  }
}
